import { driver } from "driver.js";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CompaniesContext } from "../context/companies";

/**
 * Hook para gerenciar o os parâmetros dinâmicos com o driver.js.
 *
 * @param {string} pageKey - Chave única para identificar a página e salvar o progresso no localStorage.
 * @param {string} previousKey - Chave que indica qual é a chave do passo anterior.
 * @param {string} currentStep - Representa o número do passo atual no tutorial.
 * Usado para exibir o progresso ao usuário no formato "X de Y" (ex: "7 de 18")
 * @param {array} steps - Lista de etapas do tutorial, contendo os elementos e descrições.
 * @param {function} onNextButtonClick - Função que passa para o próximo passo do tutorial.
 */

const useTutorial = ({
  pageKey,
  previousKey,
  steps,
  currentStep,
  onNextButtonClick,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    const tutorials = JSON.parse(localStorage.getItem("tutorials")) || {};
    const tutorialFinalizado = tutorials[pageKey]?.finalizado || false;
    const savedStep = tutorials[pageKey]?.step || 0;

    if (!tutorialFinalizado) {
      startTour(savedStep);
    }
  });

  const { tutorialTotalSteps } = useContext(CompaniesContext);

  const startTour = (startStep = 0) => {
    const driverObj = new driver({
      showProgress: true,
      allowClose: false,
      overlayClickNext: false,
      showButtons: ["next"],
      doneBtnText: "Anterior",
      progressText: `${currentStep} de ${tutorialTotalSteps}`,
      onPopoverRender: (popover) => {
        const firstButton = document.createElement("button");
        firstButton.innerText = "Próximo";
        firstButton.style.border = "1px solid #005eb8";
        firstButton.style.color = "#005eb8";
        firstButton.style.minWidth = "71px";
        firstButton.style.display = "flex";
        firstButton.style.fontSize = "13px";
        firstButton.style.alignItems = "center";
        firstButton.style.justifyContent = "center";

        if (onNextButtonClick) {
          firstButton.addEventListener("click", () =>
            onNextButtonClick(driverObj, navigate)
          );
        }

        popover.footerButtons.appendChild(firstButton);

        const finishButton = document.createElement("button");
        finishButton.innerText = "×";
        finishButton.setAttribute("aria-label", "Fechar tutorial");

        finishButton.style.position = "absolute";
        finishButton.style.top = "1px";
        finishButton.style.right = "1px";
        finishButton.style.background = "transparent";
        finishButton.style.border = "none";
        finishButton.style.fontSize = "20px";
        finishButton.style.color = "#005eb8";
        finishButton.style.cursor = "pointer";
        finishButton.style.zIndex = "9999";

        finishButton.addEventListener("click", () => {
          const tutorials = JSON.parse(localStorage.getItem("tutorials")) || {};
          Object.keys(tutorials).forEach((key) => {
            tutorials[key] = { ...tutorials[key], finalizado: true };
          });

          tutorials.isFinished = true;

          localStorage.setItem("tutorials", JSON.stringify(tutorials));
          driverObj.destroy();
          navigate("/planejadora-sebrae");
        });

        popover.footerButtons.appendChild(finishButton);
      },
      steps,
      onHighlightStarted: () => {
        const tutorials = JSON.parse(localStorage.getItem("tutorials")) || {};
        tutorials[pageKey] = {
          ...tutorials[pageKey],
          step: driverObj.getActiveIndex(),
        };
        localStorage.setItem("tutorials", JSON.stringify(tutorials));
      },
      onDestroyed: () => {
        const tutorials = JSON.parse(localStorage.getItem("tutorials")) || {};

        if (previousKey) {
          tutorials[previousKey] = {
            ...tutorials[previousKey],
            finalizado: false,
          };
        }

        tutorials[pageKey] = { ...tutorials[pageKey], finalizado: true };
        localStorage.setItem("tutorials", JSON.stringify(tutorials));

        const botaoAnt = document.querySelector("#botaoVoltarTutorial button");
        if (botaoAnt) {
          botaoAnt.click();
        }
      },
    });

    driverObj.drive(startStep);
  };
};

export default useTutorial;
